import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";

import PaginationV2 from "../Pagination/V2";

const baseHeaderHeight = 32;
const baseRowHeight = 40;
const heightCorrectionPixel = 5;
const loadingTemplate = `<span class="ag-overlay-loading-center">Loading...</span>`;
const noRowsTemplate = `<span>데이터가 없습니다.</span>`;
const baseDefaultColDef = {
    lockPosition: true,
    cellClass: "text-center",
    headerClass: "bg-dark text-light",
    sortable: true,
};

const basePagingInfo = { perPage: 30, maxShowPage: 10 };

const AgGridReactEx = forwardRef((props, ref) => {
    const [gridApi, setGridApi] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(props.paginationPageSize || basePagingInfo.perPage);

    const total = props.rowData ? props.rowData.length : 0;
    const pagination = props.pagination || false;
    const suppressPaginationPanel = props.suppressPaginationPanel || true;
    const paginationPageSize = props.paginationPageSize || basePagingInfo.perPage;

    useEffect(() => {
        if (!pagination) {
            setCurrentPageSize(total || props.height);
        } else {
            let minPageSize = 0;
            if (total < paginationPageSize) {
                minPageSize = total;
            } else if (paginationPageSize > total - currentPage * paginationPageSize) {
                minPageSize = total - currentPage * paginationPageSize;
            } else {
                minPageSize = props.paginationPageSize || basePagingInfo.perPage;
            }
            if (minPageSize < 5) {
                minPageSize = 5;
            }
            setCurrentPageSize(minPageSize);
        }
        return () => {};
    }, [props, currentPage]);

    const headerHeight = props.headerHeight ? props.headerHeight : baseHeaderHeight;
    const rowHeight = props.rowHeight ? props.rowHeight : baseRowHeight;
    const height = props.height ? props.height : headerHeight + rowHeight * currentPageSize + heightCorrectionPixel;
    const defaultColDef = { ...baseDefaultColDef, ...props.defaultColDef };

    const onGridReady = (params) => {
        setGridApi(params.api);
        if (!props.suppressSizeColumnsToFit) {
            params.api.sizeColumnsToFit();
        }
        if (props.onGridReady) {
            props.onGridReady(params);
        }
    };
    const onRowDataChanged = (params) => {
        if (pagination) {
            onPaging(0);
        } else {
            if (!props.suppressSizeColumnsToFit) {
                params.api.sizeColumnsToFit();
            }
        }
        if (props.onRowDataChanged) {
            props.onRowDataChanged(params);
        }
    };
    const onSortChanged = (params) => {
        params.api.refreshCells();
        if (props.onSortChanged) {
            props.onSortChanged(params);
        }
    };
    const onPaging = (page) => {
        if (gridApi) {
            setCurrentPage(page);
            gridApi.paginationGoToPage(page);
            if (!props.suppressSizeColumnsToFit) {
                gridApi.sizeColumnsToFit();
            }
        }
    };
    useImperativeHandle(ref, () => ({
        getCurrentPage() {
            return currentPage;
        },
        goPage(page) {
            onPaging(page);
        },
        sizeColumnsToFit(page) {
            if (gridApi) {
                gridApi.sizeColumnsToFit();
            }
        },
    }));

    return (
        <>
            <div className="row">
                <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 ag-theme-balham" style={{ height: `${height}px` }}>
                    <AgGridReact
                        {...props}
                        defaultColDef={defaultColDef}
                        sortingOrder={[null, 'desc', 'asc']}
                        suppressDragLeaveHidesColumns={true}
                        paginationPageSize={paginationPageSize}
                        suppressPaginationPanel={suppressPaginationPanel}
                        headerHeight={headerHeight}
                        rowHeight={rowHeight}
                        onGridReady={onGridReady}
                        onRowDataChanged={onRowDataChanged}
                        onSortChanged={onSortChanged}
                        overlayLoadingTemplate={props.overlayLoadingTemplate || loadingTemplate}
                        overlayNoRowsTemplate={props.overlayNoRowsTemplate || noRowsTemplate}
                    />
                </div>
            </div>
            {pagination && suppressPaginationPanel && (
                <div className="row">
                    {props.excelDownload && (
                        <div className="col-xl-3 col-lg-3 col-md-4 col-sm-4 col-12 mt-2">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => props.excelDataHandler()}>
                                <i className="fas fa-table mr-1"></i>다운로드
                            </button>
                        </div>
                    )}
                    <div className={`${props.excelDownload ? "col-xl-9 col-lg-9 col-md-8 col-sm-8 col-12 mt-2" : "col"} text-center text-muted d-flex justify-content-end`}>
                        <PaginationV2 perPage={paginationPageSize} maxShowPage={basePagingInfo.maxShowPage} currentPage={currentPage} total={total} callback={onPaging} />
                    </div>
                </div>
            )}
        </>
    );
});

export default AgGridReactEx;
